/**
 * ProgressIndicator Component
 * Full-width horizontal step indicator with labels, rendered above the
 * booking layout so bookers always see how many steps remain.
 */

import React from 'react';
import { Check } from 'lucide-react';
import { StepInfo } from '../utils/bookingStepConfig.js';

interface ProgressIndicatorProps {
  stepInfo: StepInfo;
  /** One label per step; length should equal stepInfo.totalSteps. */
  stepLabels?: string[];
}

export function ProgressIndicator({ stepInfo, stepLabels }: ProgressIndicatorProps) {
  const steps = Array.from({ length: stepInfo.totalSteps }, (_, i) => i + 1);

  return (
    <nav aria-label={`Step ${stepInfo.stepNumber} of ${stepInfo.totalSteps}`} className="w-full">
      <ol className="flex items-start justify-center">
        {steps.map((step, index) => {
          const isComplete = step < stepInfo.stepNumber;
          const isCurrent = step === stepInfo.stepNumber;
          const label = stepLabels?.[index];

          return (
            <li key={step} className="flex items-start flex-1 max-w-40 min-w-0">
              <div className="flex flex-col items-center gap-1.5 min-w-0 w-full">
                <div className="flex items-center w-full">
                  {/* Leading connector (invisible for first step, keeps circles centered) */}
                  <div
                    className={`h-0.5 flex-1 ${
                      index === 0 ? 'bg-transparent' : isComplete || isCurrent ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                  <div
                    className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      isComplete
                        ? 'bg-blue-600 text-white'
                        : isCurrent
                          ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                          : 'bg-gray-200 text-gray-500'
                    }`}
                    aria-current={isCurrent ? 'step' : undefined}
                  >
                    {isComplete ? <Check className="w-4 h-4" /> : step}
                  </div>
                  {/* Trailing connector (invisible for last step) */}
                  <div
                    className={`h-0.5 flex-1 ${
                      index === steps.length - 1 ? 'bg-transparent' : isComplete ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                </div>
                {label && (
                  <span
                    className={`text-xs text-center truncate w-full px-1 ${
                      isCurrent ? 'text-blue-700 font-semibold' : isComplete ? 'text-gray-700' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
